import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { LocalizationService } from 'app/services/localization/localization.service';
import { IdentityService } from 'app/services/authentication/identity.service';
import { CarrierService } from 'app/services/carrier/carrier.service';
import { SortEnum } from 'app/services/enum/sortEnum.enum';
import { ChatNotificationsService } from 'app/services/notifications/chat/notifications-chat.services';
import { UserTrailService } from 'app/services/users/user-trail.services';
import { UserTrailActionTypes } from 'app/services/users/enums/user-trail-action-types.enum';

type ChatNoReadMap = Record<string, number>;

@Component({
  selector: 'app-notifications-chat',
  templateUrl: './notifications-chat.component.html',
  styleUrls: ['./notifications-chat.component.scss'],
})
export class NotificationsChatComponent implements OnInit, OnDestroy {
  @ViewChild('msgScroller') msgScroller?: ElementRef<HTMLDivElement>;
  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;

  private destroy$ = new Subject<void>();

  // Audio / recorder
  recorder: MediaRecorder | null = null;
  recording = false;
  audioChunks: Blob[] = [];
  private audio: HTMLAudioElement | null = null;

  // UI state
  isDropdownOpen = false;
  isStarted = true; // evita sonido al iniciar
  isSending = false;
  isNotificacionEnable = true;
  isLoadingRefresh = false;

  // Counters
  messageNoReadQty = 0;
  chatNumbers: { driverId: string; value: string }[] = [];

  // Drivers
  allDriversInCarrier: any[] = [];
  allDrivers: any[] = [];
  searchTerm = '';
  filtersForDriversInCarrier: { alias: string; value: any }[] = [];
  column = 'id';
  sort: SortEnum = SortEnum.DESC;
  reportCode = '079';

  // Selection / chat
  carrierId = this._identityService.userId;
  selectedDriver: number | '' = '';
  selectedDriverName: string | null = null;
  selectedOperatorName: string = this._identityService.subUserName;
  chatMessages: any[] = [];
  selectedChat: any[] = [];
  newMessage = '';

  // Date
  pipe: DatePipe;
  dateFormat!: string;

  // Timer (audio)
  recordingStartTime: number | null = null;
  seconds = 0;
  timer: any;

  constructor(
    private _notificationsService: ChatNotificationsService,
    private _translateService: TranslateService,
    private _carrierService: CarrierService,
    public _localizationService: LocalizationService,
    private _identityService: IdentityService,
    public cd: ChangeDetectorRef,
    private _userTrailService: UserTrailService
  ) {
    this.pipe = new DatePipe(
      this._localizationService.localeData?.locale ?? 'en-US'
    );
  }

  // ===== Lifecycle =====
  ngOnInit(): void {
    this.isNotificacionEnable = this._identityService.getIsNotificacionEnable();
    this.prepareAudio();
    this.onResize(); // inicializa medidas

    this.onLangChange();
    this._translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onLangChange());

    this.getAll();
    this.getAllDriversInCarrier();
    this.checkMessageNoRead();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cd.detach();
  }

  // ===== Lang / format =====
  onLangChange(): void {
    this.dateFormat = this._localizationService.localeData?.dateFormat ?? 'MM/dd/yyyy';
  }

  // ===== Resize =====
  screenHeight = 0;
  screenWidth = 0;

  @HostListener('window:resize')
  onResize(): void {
    this.screenHeight = window.innerHeight;
    this.screenWidth = window.innerWidth;
  }

  // ===== No-read watcher =====
  private checkMessageNoRead(): void {
    this._notificationsService
      .getChatsNoReading(this._identityService.userId) // ← ya es Observable<ChatNoReadMap | null>
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: ChatNoReadMap | null) => {
        if (!data) return;

        const next = Object.entries(data).map(([driverId, value]) => ({
          driverId,
          value: String(value ?? 0),
        }));

        if (this.isNotificacionEnable && !this.isStarted && this.chatNumbers?.length) {
          const increased = this.chatNumbers.some((prev) => {
            const now = next.find((n) => n.driverId === prev.driverId);
            return now && Number(now.value) > Number(prev.value);
          });

          const selectedHasUnread =
            this.selectedDriver !== '' &&
            this.chatNumbers.some(
              (c) => c.driverId === String(this.selectedDriver) && Number(c.value) > 0
            );

          if (increased && !selectedHasUnread) {
            this.audio?.play().catch((e) => console.log('Audio error', e));
          }
        }
        this.isStarted = false;

        this.chatNumbers = next;
        this.messageNoReadQty = Object.values(data).reduce((a, b) => a + (b ?? 0), 0);

        // ordena drivers por no leídos desc
        this.allDrivers.sort((a, b) => {
          const aId = a.row.Col1;
          const bId = b.row.Col1;
          const aVal = data[String(aId)] ?? 0;
          const bVal = data[String(bId)] ?? 0;
          return bVal - aVal;
        });

        this.getAll();

        if (this.selectedDriver !== '' && this.selectedChat?.length) {
          this.getChatDataAndDetails(this.selectedDriver);
        }
      }
    );
  }


  // ===== Totales por driver =====
  private getAll(): void {
    this._notificationsService
      .getAllChats(this._identityService.userId, 'CARRIER')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Si necesitás guardar un diccionario driverId->chats, hacelo acá.
      });
  }

  // ===== Drivers =====
  getAllDriversInCarrier(): void {
    this.filtersForDriversInCarrier = [{ alias: 'carrierId', value: this._identityService.userId }];
    this.isLoadingRefresh = true;

    this._carrierService
      .getGenericReport(1, 1_000_000, this.column, this.sort, this.reportCode, this.filtersForDriversInCarrier)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          this.isLoadingRefresh = false;
          this.allDriversInCarrier = response.content ?? [];
          this.allDrivers = this.allDriversInCarrier;
        },
        () => (this.isLoadingRefresh = false)
      );
  }

  updateFilter(): void {
    const term = (this.searchTerm ?? '').toLowerCase().trim();
    this.allDrivers = term
      ? this.allDriversInCarrier.filter(
          (i) =>
            String(i.row.Col3).toLowerCase().includes(term) ||
            String(i.row.Col2).toLowerCase().includes(term)
        )
      : this.allDriversInCarrier;
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.updateFilter();
  }

  // ===== Chat =====
  private async getChatDataPromise(driverId: number): Promise<void> {
    const data: any = await firstValueFrom(
      this._notificationsService
        .getAllChatContent('CARRIER', this.carrierId, driverId)
        .pipe(takeUntil(this.destroy$))
    );
    this.chatMessages = data.content ?? [];
  }

  getChatDataAndDetails(driverId: number | ''): void {
    if (driverId === '') return;

    this._notificationsService
      .getAllChatContent('CARRIER', this.carrierId, driverId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.chatMessages = data.content ?? [];
        this.selectedDriver = driverId;

        const driver = this.allDrivers.find((d) => d.row.Col1 === driverId);
        this.selectedDriverName = driver?.row?.Col3 ?? '';

        const list = this.chatMessages.filter((c: any) => c.driverId === Number(driverId));
        this.selectedChat = list.sort(
          (a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );

        this.scrollToBottom();
      });
  }

  getChatValue(driverId: string): string {
    const item = this.chatNumbers?.find((c) => c.driverId === driverId);
    return item && item.value !== '0' ? item.value : '';
  }

  async sendChatMessage(driverId: number, message: string): Promise<void> {
    const text = (message ?? '').trim();
    if (this.isSending || !text) return;

    this.isSending = true;

    const driver = this.allDrivers.find((d) => d.row.Col1 === driverId);
    this.selectedDriverName = driver?.row?.Col3 ?? '';
    this.selectedOperatorName = this._identityService.subUserName;

    try {
      await firstValueFrom(
        this._notificationsService
          .sendChatMessage({
            recipient: 'DRIVER',
            sender: 'CARRIER',
            driverId,
            carrierId: +this.carrierId,
            message: text,
            driverName: this.selectedDriverName,
            operatorName: this.selectedOperatorName,
            tokenRecipient: driver?.row?.Col6 ?? '',
            platform: driver?.row?.Col7 ?? '',
          })
          .pipe(tap(() => (this.newMessage = '')))
      );

      await this.getChatDataPromise(driverId);
      this.getChatDataAndDetails(driverId);
    } finally {
      this.isSending = false;
    }
  }

  async sendVoiceChatMessage(
    driverId: number,
    mediaBase64: string | ArrayBuffer | null,
    lengthMs: number
  ): Promise<void> {
    const driver = this.allDrivers.find((d) => d.row.Col1 === driverId);
    this.selectedDriverName = driver?.row?.Col3 ?? '';
    this.selectedOperatorName = this._identityService.subUserName;

    // --- Normaliza media a string (data URL) ---
    const mediaStr =
      typeof mediaBase64 === 'string'
        ? mediaBase64
        : mediaBase64 instanceof ArrayBuffer
          ? 'data:audio/webm;base64,' + btoa(String.fromCharCode(...new Uint8Array(mediaBase64)))
          : '';

    // Si tu API quiere SOLO el base64 sin encabezado, usa:
    // const mediaOnlyB64 = mediaStr.replace(/^data:.*;base64,/, '');

    await firstValueFrom(
      this._notificationsService
        .sendVoiceChatMessage({
          recipient: 'DRIVER',
          sender: 'CARRIER',
          driverId,
          carrierId: +this.carrierId,
          media: mediaStr,              // ← string garantizado
          lenght: String(lengthMs),     // ← a string si la interfaz así lo pide
          driverName: this.selectedDriverName,
          operatorName: this.selectedOperatorName,
          tokenRecipient: driver?.row?.Col6 ?? '',
          platform: driver?.row?.Col7 ?? '',
        })
        .pipe(tap(() => (this.newMessage = '')))
    );

    await this.getChatDataPromise(driverId);
    this.getChatDataAndDetails(driverId);
  }

  // ===== Recorder =====
  startRecording(): void {
    this.recordingStartTime = Date.now();

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        this.recorder = new MediaRecorder(stream);
        this.audioChunks = [];

        this.recorder.addEventListener('dataavailable', (event: BlobEvent) => {
          this.audioChunks.push(event.data);
        });

        this.recorder.start();
        this.recording = true;
        this.timer = setInterval(() => this.seconds++, 1000);
      })
      .catch((err) => console.error('Error al acceder al micrófono:', err));
  }

  stopRecording(): void {
    if (!this.recorder || this.recorder.state !== 'recording') return;

    this.recorder.addEventListener('stop', () => {
      const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
      const reader = new FileReader();
      reader.onloadend = () => {
        const audioBase64 = reader.result;
        const duration = Date.now() - (this.recordingStartTime ?? Date.now());
        if (this.selectedDriver !== '') {
          this.sendVoiceChatMessage(this.selectedDriver as number, audioBase64, duration);
        }
        this.audioChunks = [];
        this.recorder = null;
      };
      reader.readAsDataURL(audioBlob);
    });

    this.recorder.stop();
    this.recording = false;
    clearInterval(this.timer);
    this.seconds = 0;
  }

  discardRecording(): void {
    if (!this.recorder || this.recorder.state !== 'recording') return;

    this.recorder.addEventListener('stop', () => {
      this.audioChunks = [];
      this.recorder = null;
    });

    this.recorder.stop();
    this.recording = false;
    clearInterval(this.timer);
    this.seconds = 0;
  }

  formatTime(totalSeconds: number): string {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  }

  // ===== Dropdown =====
  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;

    if (!this.isDropdownOpen) {
      this.onChatClose();
    } else {
      this._userTrailService
        .register({
          carrierId: parseFloat(this._identityService.userId),
          actionCode: 'MSG',
          actionType: UserTrailActionTypes.READ,
          businessData: null,
          userId: parseFloat(this._identityService.subUserId),
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {});
    }
  }

  onChatClose(): void {
    this.selectedDriver = '';
    this.selectedDriverName = null;
    this.selectedChat = [];
    this.isDropdownOpen = false;
  }

  onClickNotificacionEnable(): void {
    this.isNotificacionEnable = !this.isNotificacionEnable;
    this._identityService.setIsNotificacionEnable(this.isNotificacionEnable);
  }

  private prepareAudio(): void {
    const el = new Audio('assets/media/chat.mp3');
    el.load();
    el.addEventListener('canplaythrough', () => (this.audio = el));
  }

  private scrollToBottom() {
    setTimeout(() => {
      const el = this.msgScroller?.nativeElement;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
    });
  }

  // trackBy para listas
  trackByDriver = (_: number, item: any) => item?.row?.Col1;
  trackByChat = (_: number, item: any) => item?.id ?? item?.timestamp ?? _;
}
